import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfile } from '@/hooks/useUserProfile';

export interface Product {
  id: string;
  tenant_id: string;
  name: string;
  category: 'physical' | 'digital' | 'service' | 'subscription';
  product_type: 'ebook' | 'course' | 'saas';
  target_audience: 'b2c' | 'b2b' | 'b2b2c';
  logo_url: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  data_source: 'manual' | 'integration' | 'upload';
  gateway_product_id: string | null;
}

interface ProductContextType {
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product | null) => void;
  products: Product[];
  isLoading: boolean;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export function ProductProvider({ children }: { children: ReactNode }) {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { tenant } = useUserProfile();

  useEffect(() => {
    if (!tenant?.id) return;

    const fetchProducts = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching products:', error);
        setIsLoading(false);
        return;
      }

      setProducts(data as Product[]);

      // Set default product from user settings or first product
      if (data && data.length > 0) {
        const { data: settings } = await supabase
          .from('user_settings')
          .select('default_product_id')
          .single();

        const defaultProduct = data.find(p => p.id === settings?.default_product_id) || data[0];
        setSelectedProduct(defaultProduct as Product);
      } else {
        // Se não há produtos ativos, limpar a seleção
        setSelectedProduct(null);
      }

      setIsLoading(false);
    };

    fetchProducts();

    // Subscribe to realtime changes in products table
    const channel = supabase
      .channel('products-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'products',
          filter: `tenant_id=eq.${tenant.id}`,
        },
        () => {
          // Refetch products when any change occurs
          fetchProducts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenant?.id]);

  return (
    <ProductContext.Provider value={{ selectedProduct, setSelectedProduct, products, isLoading }}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProduct() {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
}
